import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";

const CitizenDistributionTable = () => {
  const pid = useSelector((state) => state.user.projectId);

  // ================= FILTER STATES =================
  const [orgOptions, setOrgOptions] = useState([]);
  const [caseDistrictOptions, setCaseDistrictOptions] = useState([]);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedCaseDistrict, setSelectedCaseDistrict] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    orgs: [],
    caseDistricts: [],
    startDate: "",
    endDate: ""
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ================= QUERY PARAMS =================
  const getQueryParams = () => {
    const params = new URLSearchParams();

    if (pid) params.append("pid", pid);

    appliedFilters.orgs.forEach(o => params.append("OrgName", o));
    appliedFilters.caseDistricts.forEach(d => params.append("CaseDistrict", d));

    if (appliedFilters.startDate) params.append("start_date", appliedFilters.startDate);
    if (appliedFilters.endDate) params.append("end_date", appliedFilters.endDate);

    return params.toString();
  };

  // ================= FETCH FILTER OPTIONS =================
  const fetchFilters = async () => {
    try {
      const params = getQueryParams();

      const [orgRes, districtRes] = await Promise.all([
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-org/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-case-districts/?${params}`)
      ]);

      const orgData = await orgRes.json();
      const districtData = await districtRes.json();

      setOrgOptions(
        orgData.unique_Organizations.map(o => ({ value: o, label: o }))
      );

      setCaseDistrictOptions(
        districtData.unique_case_districts.map(d => ({ value: d, label: d }))
      );

    } catch (err) {
      console.error("Filter fetch error:", err);
    }
  };

  // ================= FETCH TABLE DATA =================
  const fetchData = async () => {
    setLoading(true);

    try {
      const params = getQueryParams();

      const [funnelRes, citizenRes] = await Promise.all([
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/funnel/chart/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/unique-citizen-distribution/?${params}`)
      ]);

      const funnel = await funnelRes.json();
      const citizen = await citizenRes.json();

      const tableData = [
        {
          status: "Open",
          cases: funnel.application_open || 0,
          citizens: funnel.citizen_open_count || 0,
          newly: citizen.newly_citizen_open_count || 0
        },
        {
          status: "DS",
          cases: funnel.docket_submitted || 0,
          citizens: funnel.citizen_ds_count || 0,
          newly: citizen.newly_citizen_ds_count || 0
        },
        {
          status: "BR",
          cases: funnel.br_count || 0,
          citizens: funnel.citizen_br_count || 0,
          newly: citizen.newly_citizen_br_count || 0
        }
      ];

      setData(tableData);

    } catch (err) {
      console.error("Data fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= APPLY FILTERS =================
  const applyFilters = () => {
    setAppliedFilters({
      orgs: selectedOrgs,
      caseDistricts: selectedCaseDistrict,
      startDate,
      endDate
    });
  };

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedCaseDistrict([]);
    setStartDate("");
    setEndDate("");

    setAppliedFilters({
      orgs: [],
      caseDistricts: [],
      startDate: "",
      endDate: ""
    });
  };

  useEffect(() => {
    fetchFilters();
  }, [appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [appliedFilters, pid]);

  // ================= UI =================
  const MultiSelect = ({ options, value, setValue, placeholder }) => (
    <Select
      isMulti
      options={options}
      value={options.filter(o => value.includes(o.value))}
      onChange={(val) => setValue(val.map(v => v.value))}
      placeholder={placeholder}
    />
  );

  return (
    <div className="max-w-[90vw] mt-[3vh] bg-white border shadow rounded">

      {/* HEADER */}
      <div className="px-6 py-4 border-b">
        <h2 className="text-xl font-semibold text-pink-800">
          Unique Citizen Distribution
        </h2>
      </div>

      {/* FILTERS */}
      <div className="p-4 grid md:grid-cols-4 gap-4">

        <MultiSelect
          options={orgOptions}
          value={selectedOrgs}
          setValue={setSelectedOrgs}
          placeholder="Organization"
        />

        <MultiSelect
          options={caseDistrictOptions}
          value={selectedCaseDistrict}
          setValue={setSelectedCaseDistrict}
          placeholder="Case District"
        />

        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          className="border p-2 rounded"
        />

        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          className="border p-2 rounded"
        />

        <div className="flex gap-3 md:col-span-4">
          <button
            onClick={clearFilters}
            className="bg-red-700 text-white px-4 py-2 rounded"
          >
            Clear
          </button>

          <button
            onClick={applyFilters}
            className="bg-blue-700 text-white px-4 py-2 rounded"
          >
            Apply Filters
          </button>
        </div>

      </div>

      {/* TABLE */}
      <div className="p-6">

        {loading ? (
          <div>Loading...</div>
        ) : (
          <table className="w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2">Status</th>
                <th className="border p-2">Unique Citizens</th>
                <th className="border p-2">Number of Cases</th>
                {/* <th className="border p-2">Citizens (Case Based)</th> */}
              </tr>
            </thead>

            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="text-center">
                  <td className="border p-2 font-semibold">{row.status}</td>
                  <td className="border p-2 text-green-600 font-bold">
                    {row.newly.toLocaleString()}
                  </td>
                  <td className="border p-2">{row.cases.toLocaleString()}</td>
                  {/* <td className="border p-2">{row.citizens.toLocaleString()}</td> */}
                  
                </tr>
              ))}
            </tbody>
          </table>
        )}

      </div>

    </div>
  );
};

export default CitizenDistributionTable;